import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  MyLocation,
  WorkOutline,
  PersonPinCircle,
  Search,
  LockOutlined
} from '@mui/icons-material';

/**
 * Friendly explanation shown before requesting browser location permission.
 *
 * The dialog itself does NOT request the permission; the parent decides what to
 * do via onEnable (e.g. call useGeolocation().requestLocation()) and onNotNow.
 *
 * Props:
 *  - open: boolean
 *  - onEnable: () => void        // user pressed "Enable Location"
 *  - onNotNow: () => void        // user pressed "Not Now" (or closed)
 *  - loading: boolean            // disable buttons while fetching location
 */
const LocationPermissionDialog = ({ open, onEnable, onNotNow, loading = false }) => {
  const points = [
    { icon: <WorkOutline fontSize="small" />, text: 'Find nearby jobs' },
    { icon: <PersonPinCircle fontSize="small" />, text: 'Help employers discover nearby workers' },
    { icon: <Search fontSize="small" />, text: 'Improve search accuracy' }
  ];

  return (
    <Dialog
      open={open}
      onClose={() => { if (!loading) onNotNow?.(); }}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MyLocation sx={{ color: '#3D2B1F' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A1A1A' }}>
            Enable Location
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: '#555', mb: 1.5 }}>
          ULCAAPP uses your location to:
        </Typography>
        <List dense disablePadding>
          {points.map((p) => (
            <ListItem key={p.text} disableGutters sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 34, color: '#5C3D2E' }}>{p.icon}</ListItemIcon>
              <ListItemText
                primary={p.text}
                primaryTypographyProps={{ sx: { fontSize: '0.9rem', color: '#333' } }}
              />
            </ListItem>
          ))}
        </List>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, p: 1.25, bgcolor: '#F3ECE6', borderRadius: 1.5 }}>
          <LockOutlined sx={{ fontSize: 18, color: '#5C3D2E' }} />
          <Typography variant="caption" sx={{ color: '#5C3D2E' }}>
            Your exact location will never be shown publicly.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => onNotNow?.()}
          disabled={loading}
          sx={{ color: '#5C3D2E', textTransform: 'none', fontWeight: 600 }}
        >
          Not Now
        </Button>
        <Button
          onClick={() => onEnable?.()}
          disabled={loading}
          variant="contained"
          sx={{
            bgcolor: '#3D2B1F',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 1.5,
            boxShadow: 'none',
            '&:hover': { bgcolor: '#2C1F17', boxShadow: 'none' }
          }}
        >
          {loading ? 'Getting location…' : 'Enable Location'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationPermissionDialog;
